import { Icon, liProperties, TooltipHost } from "@fluentui/react";
import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppConfig } from "../../Config";
import { AppRoutes } from "../../Constants/AppRoutes";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import PingUtil from "../../Utils/PingUtil";
import {
  getSensorIconName,
  getSensorIconTooltip,
} from "../../Utils/SensorMaps";
import SettingsUtil from "../../Utils/Settings";
import { ToastMessage } from "../../Utils/UIMessages";
import IconButtonItem from "../IconButtonItem/IconButtonItem";
import VideoFrame from "../VideoFrame/VideoFrame";
import "./MonitoringCardSmartBed.scss";
import {
  DEFAULT_VIDEO_SIZES,
  developerUI,
} from "../../Pages/OptionsPage/OptionsPage";
import { JSONPrint } from "../../Utils/Generic";
import { useDispatch } from "react-redux";
import WalkieTalkieControls from "../WalkieTalkieControls/WalkieTalkieControls";
import {
  isSensorOffline,
  getOfflineTooltipContent,
} from "../../Utils/SensorStatus";

export interface IMonitoringCardSmartBedProps {
  sentinel: ISentinelRich;
  index: number;
}

const MonitoringCardSmartBed = (props: IMonitoringCardSmartBedProps) => {
  const navigate = useNavigate();
  const [lightOn, setLightOn] = useState<boolean>(false);
  const [, setTick] = useState<number>(0);

  // Timer per forzare re-render periodico e aggiornare lo stato offline
  useEffect(() => {
    const checkIntervalMs = AppConfig.sensor.offlineCheckInterval * 1000;
    
    const interval = setInterval(() => {
      setTick((prev: number) => prev + 1);
    }, checkIntervalMs);
    
    return () => clearInterval(interval);
  }, []);

  // Check if any enabled sensor is offline (INCLUDE pesogr2!)
  const hasOfflineSensors = props.sentinel.sensors
    //.filter((x: ISensor) => x.enabled && x.type !== "pesogr2")
    .filter((x: ISensor) => x.enabled)
    .some((x: ISensor) => isSensorOffline(x));

  const disableLight = () => {
    ApiService.SentinelController.UpdateLedStatus(
      props.sentinel.id.toString(),
      false,
      (response: IAPIResponse) => {
        if (response.error === null) {
          setLightOn(false);
          ToastMessage(
            "success",
            "Led spento sul dispositivo: " + props.sentinel.identifier
          );
        } else {
          ToastMessage(
            "error",
            "Impossibile accendere il led sul dispositivo: " +
              props.sentinel.identifier
          );
        }
        setTimeout(() => {
          setLightOn(false);
        }, 5000);
      }
    );
  };

  const enableLight = () => {
    ApiService.SentinelController.UpdateLedStatus(
      props.sentinel.id.toString(),
      true,
      (response: IAPIResponse) => {
        if (response.error === null) {
          setLightOn(true);
          ToastMessage(
            "success",
            "Led acceso sul dispositivo: " + props.sentinel.identifier
          );
        } else {
          ToastMessage(
            "error",
            "Impossibile accendere il led sul dispositivo: " +
              props.sentinel.identifier
          );
        }
        setTimeout(() => {
          setLightOn(false);
        }, 5000);
      }
    );
  };

  const getCardPatientDescriptor = () => {
    return [
      <div key={0} className="monitoring-card-tooltip-hint-row">
        <div className="monitoring-card-tooltip-hint">Ospite: </div>
        {props.sentinel.patient.identifier}
      </div>,
      <div key={1} className="monitoring-card-tooltip-hint-row">
        <div className="monitoring-card-tooltip-hint">Zona: </div>
        {String(props.sentinel.patient.floor).padStart(2, "0")}
      </div>,
      <div key={2} className="monitoring-card-tooltip-hint-row">
        <div className="monitoring-card-tooltip-hint">Stanza: </div>
        {String(props.sentinel.patient.room).padStart(2, "0")}
      </div>,
      <div key={3} className="monitoring-card-tooltip-hint-row">
        <div className="monitoring-card-tooltip-hint">Letto: </div>
        {String(props.sentinel.patient.bed).padStart(2, "0")}
      </div>,
    ];
  };

  return (
    <div className="monitoring-card-wrapper">
      <div className="monitoring-card-header">
        <div className="monitoring-card-icon-zone">
          {hasOfflineSensors && (
            <TooltipHost
              content="Uno o più sensori sono offline - Verificare alimentazione, connessione WiFi o malfunzionamento"
              id={`bed_offline_icon_${props.sentinel.id}`}
            >
              <div className="monitoring-card-warning-badge-icon">
                <span className="warning-triangle">⚠️</span>
              </div>
            </TooltipHost>
          )}
          <img src="./assets/bed-monitoring-icon.png" />
        </div>

        <div className="monitoring-card-header-buttons">
          {!lightOn && (
            <div>
              <IconButtonItem
                iconName="Flashlight"
                tooltip="Accendi led"
                onClick={() => {
                  enableLight();
                }}
              />
            </div>
          )}
          {lightOn && (
            <div>
              <IconButtonItem
                iconName="VideoLightOff"
                tooltip="Spengi led"
                onClick={() => {
                  disableLight();
                }}
              />
            </div>
          )}
          <WalkieTalkieControls
            sentinelId={props.sentinel.id}
            compactMode={false}
            activatedBy="manual"
          />
          <div>
            <IconButtonItem
              iconName="Settings"
              tooltip="Configura Letto"
              onClick={() => {
                navigate(
                  AppRoutes.APP_CONFIGURE_PAGE +
                    "?sentinelId=" +
                    props.sentinel.id
                );
              }}
            />
          </div>
        </div>
      </div>

      <div className="monitoring-card-label-header">
        <div className="monitoring-card-header-top">
          <div className="monitoring-card-max-width-crop">
            {props.sentinel.patient.identifier}
          </div>
        </div>
        <div className="monitoring-card-header-row">
          {false && (
            <div>
              Zona: {String(props.sentinel.patient.floor).padStart(2, "0")}
            </div>
          )}
          <div>ID Letto: {String(props.sentinel.id)}</div>
          <div>
            Stanza: {String(props.sentinel.patient.room).padStart(2, "0")}
          </div>

          {false && (
            <div>L: {String(props.sentinel.patient.bed).padStart(2, "0")}</div>
          )}
        </div>
      </div>

      <div className="monitoring-card-icon-wrap">
        {props.sentinel.sensors
          .filter((x: ISensor) => x.type !== "pesogr2")
          .map((x: ISensor, i: number) => {
            const isOffline = isSensorOffline(x);
            const tooltipContent = isOffline
              ? getOfflineTooltipContent(x, getSensorIconTooltip(x.type))
              : getSensorIconTooltip(x.type);

            return (
              <TooltipHost
                key={i}
                content={tooltipContent}
                id={props.sentinel.id + "_" + x.id + "_sensor_on_sentinel"}
              >
                <div className="monitoring-card-outer-wrap">
                  {!x.enabled && (
                    <div className="monitoring-card-deny-sign-wrap">
                      <img
                        className="monitoring-card-deny-sign"
                        src="./assets/denysign.png"
                      />
                    </div>
                  )}
                  <div
                    className={
                      "monitoring-card-icon " +
                      (x.enabled ? "" : "monitoring-card-icon-disabled") +
                      (isOffline ? " monitoring-card-icon-offline" : "")
                    }
                  >
                    <Icon iconName={getSensorIconName(x.type)} />
                  </div>
                </div>
              </TooltipHost>
            );
          })}
      </div>
      {developerUI() && <div>{JSONPrint(props.sentinel.sensors)}</div>}
    </div>
  );
};

export default MonitoringCardSmartBed;
